/* eslint-disable lines-between-class-members */

import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const ctor = typeof globalThis !== "undefined" && (globalThis.GroundPlaneNode || globalThis.GroundPlane);
const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

const GROUND_PLANE_PROPS = ["color", "receiveShadow", "walkable"];

/* -------------------------------------------------------------------------- */
/*  A-Frame adapter                                                           */
/* -------------------------------------------------------------------------- */

class GroundPlaneAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;
    const COMPONENT = "ground-plane";
    const pending = (this._pendingUpdates = {});

    const flush = () => {
      const el = this.sourceObject?.el;
      if (!el) return;

      if (pending[COMPONENT]) {
        const current = { ...(el.getAttribute(COMPONENT) || {}) };
        Object.assign(current, pending[COMPONENT]);
        el.setAttribute(COMPONENT, current);
        delete pending[COMPONENT];
      }

      if (pending["shadow"]) {
        const current = { ...(el.getAttribute("shadow") || {}) };
        Object.assign(current, pending["shadow"]);
        el.setAttribute("shadow", current);
        delete pending["shadow"];
      }
    };

    this.sourceObject?.el?.addEventListener("componentinitialized", e => {
      const name = e?.detail?.name;
      if (name === COMPONENT || name === "shadow") flush();
    });

    const setGroundPlaneProp = (k, v) => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const attr = el.getAttribute(COMPONENT);
      if (!attr) {
        (pending[COMPONENT] ||= {})[k] = v;
      } else {
        const next = { ...attr, [k]: v };
        el.setAttribute(COMPONENT, next);
      }
    };

    const getGroundPlaneProp = k => {
      const el = this.sourceObject?.el;
      if (!el) return pending[COMPONENT]?.[k];
      const attr = el.getAttribute(COMPONENT);
      return (attr && attr[k]) ?? pending[COMPONENT]?.[k];
    };

    const setShadowProp = (k, v) => {
      const el = this.sourceObject?.el;
      if (!el) return;
      const attr = el.getAttribute("shadow");
      if (!attr) {
        (pending["shadow"] ||= {})[k] = !!v;
      } else {
        const next = { ...attr, [k]: !!v };
        el.setAttribute("shadow", next);
      }
    };

    const getShadowProp = k => {
      const el = this.sourceObject?.el;
      if (!el) return pending["shadow"]?.[k];
      const attr = el.getAttribute("shadow");
      return (attr && attr[k]) ?? pending["shadow"]?.[k];
    };

    // Map ground plane properties
    Object.defineProperty(this, "color", {
      get: () => getGroundPlaneProp("color"),
      set: v => setGroundPlaneProp("color", v)
    });

    Object.defineProperty(this, "receiveShadow", {
      get: () => getShadowProp("receive"),
      set: v => setShadowProp("receive", v)
    });

    Object.defineProperty(this, "walkable", {
      get: () => !!this.sourceObject?.el?.hasAttribute("walkable"),
      set: v => {
        const el = this.sourceObject?.el;
        if (!el) return;
        if (v) {
          el.setAttribute("walkable", "");
        } else {
          el.removeAttribute("walkable");
        }
      }
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* -------------------------------------------------------------------------- */
/*  Editor adapter                                                            */
/* -------------------------------------------------------------------------- */

class GroundPlaneEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    GROUND_PLANE_PROPS.forEach(prop => {
      const isColorProp = prop === "color";

      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          let valueToSet = v;
          
          // For color property, ensure we always set a THREE.Color on the underlying node
          if (isColorProp) {
            if (v && !v.isColor) {
              // Convert string/number colors to THREE.Color
              valueToSet = new THREE.Color(v);
            }
          }
          
          this._values.set(prop, v); // Store original value in adapter
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(this.sourceObject, valueToSet);
            } catch {
              /* ignore */
            }
          } else if (isColorProp && this.sourceObject?.color?.set) {
            this.sourceObject.color.set(valueToSet);
            this._values.set(prop, this.sourceObject.color);
          } else {
            try {
              this.sourceObject[prop] = valueToSet;
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed from source without invoking setters when possible
      const desc = originalDescriptors.get(prop);
      let initial;
      try {
        if (desc?.get) initial = desc.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of GROUND_PLANE_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  // A-Frame path if we have an element
  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new GroundPlaneAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  // Editor / plain Three path
  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", {
    value: new GroundPlaneEditorAdapter(self, descriptors),
    configurable: true
  });
  return self._adapter;
}

export class GroundPlaneElement extends ScriptableObject {
  static scriptableType = "ground-plane";

  constructor(initial = {}) {
    super();

    // A-Frame creation: build <a-entity>, adopt, and return the shifted object
    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      el.setAttribute("ground-plane", {
        color: initial.color || "#5DE336"
      });
      el.setAttribute("shadow", {
        receive: initial.receiveShadow ?? true,
        cast: false
      });
      
      if (initial.walkable !== false) {
        el.setAttribute("walkable", "");
      }

      const obj = this.constructor.adopt(el);
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy init adapter and apply initial props
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj; // IMPORTANT: return shifted object
    }

    // Editor / plain Three: construct real GroundPlaneNode and upgrade to scriptable
    const node = createGroundPlaneElementEditor(initial);
    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      
      // deep-clone where necessary to avoid shared refs
      if (val?.isColor) {
        this[prop] = val.clone();
      } else if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else {
        this[prop] = val;
      }
    };

    for (const p of GROUND_PLANE_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of logicalChildrenOf(source)) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    const init = {};

    for (const p of GROUND_PLANE_PROPS) {
      const v = this[p];
      if (v === undefined) continue;
      
      // Follow the same pattern as other implementations
      if (v?.isColor) {
        init[p] = v.clone();
      } else if (Array.isArray(v)) {
        init[p] = v.slice();
      } else {
        init[p] = v;
      }
    }

    const dest = new GroundPlaneElement(init);

    // Copy transforms/children; copy() also uses the adapter surface.
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    return dest.copy(this, recursive);
  }

  // convenience (optional)
  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade an editor GroundPlaneNode to scriptable and attach adapter + proxies
function makeEditorGroundPlaneNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || ctor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: GroundPlaneElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = GroundPlaneElement.scriptableType;
  }

  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new GroundPlaneEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new GroundPlaneEditorAdapter(node, descriptors);
  }

  for (const prop of GROUND_PLANE_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* best-effort */
    }
  }

  // Ensure GroundPlaneElement's enhanced `clone` logic is used for
  // editor-created ground plane nodes. Without this, the instance would fall back to
  // GroundPlaneNode (or Object3D) defaults which do not propagate the
  // scriptable properties managed by the adapter.
  try {
    if (node.clone !== GroundPlaneElement.prototype.clone) {
      Object.defineProperty(node, "clone", {
        value: GroundPlaneElement.prototype.clone,
        writable: true,
        configurable: true
      });
    }
  } catch {
    node.clone = GroundPlaneElement.prototype.clone;
  }

  return node;
}

// Editor factory for GroundPlane (editor/plain Three)
function createGroundPlaneElementEditor(initial = {}) {
  const Ctor = globalThis.GroundPlaneNode || globalThis.GroundPlane;
  if (typeof Ctor !== "function") {
    throw new Error("GroundPlaneElementEditor: GroundPlaneNode constructor not found.");
  }

  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new Ctor(editor);
  } catch (e) {
    throw new Error(`GroundPlaneElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorGroundPlaneNodeScriptable(node);

  // Apply initial properties with walkable default
  const initialWithDefaults = { walkable: true, ...initial };
  for (const k of Object.keys(initialWithDefaults)) {
    if (k in node) {
      // Handle special cases for initial values
      if (k === "color") {
        node[k] = new THREE.Color(initialWithDefaults[k]);
      } else {
        node[k] = initialWithDefaults[k];
      }
    }
  }
  
  return node;
}

// If you create objects via adopt(el) (constructor doesn't run),
// the accessor getter calls ensureAdapter(this) and mounts the adapter on first access.
// That's why binding these getters on the prototype is crucial—even for adopted instances.
GROUND_PLANE_PROPS.forEach(prop => {
  Object.defineProperty(GroundPlaneElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

ScriptableObjects.registerType("ground-plane", source => {
  if (!source) return source;

  // Only promote if type matches (brand can live on obj or on el.object3D)
  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;
  if (type !== GroundPlaneElement.scriptableType) return source;

  // If given an A-Frame element or a Scriptable with .el -> adopt that element
  if (isAFrameEntity(source)) return GroundPlaneElement.adopt(source);

  // Editor/plain-Three: upgrade in-place to scriptable and attach adapter + proxies
  if (isObject3D(source)) return makeEditorGroundPlaneNodeScriptable(source);

  return source;
});

// Named exports expected by external consumers -----------------------------
export { GroundPlaneAFrameAdapter, GroundPlaneEditorAdapter, GROUND_PLANE_PROPS };

